export type VideoMetadata = { duration: number; width: number };

/** Longer than a header ever takes to read; past it the browser is not going to answer. */
const READ_TIMEOUT_MS = 10_000;

const failure = (name: string, message: string): Error =>
  Object.assign(new Error(message), { name });

/**
 * The duration and the pixel width of a video file, read by the browser from
 * the file's header through a hidden <video> element. Storage rules can bound
 * size and type but not these two, so the upload waits for this.
 *
 * Rejects when the browser cannot decode the file (for a hero video mostly a
 * codec it does not play, such as HEVC from a phone), with a `TimeoutError`
 * when it neither answers nor fails within ten seconds (some browsers do
 * neither for a file they cannot open), and with an `AbortError` when the
 * caller gives up, as a card does when it unmounts mid-check. Whichever way it
 * ends the object URL is revoked, the timer is cleared and the element is told
 * to let go of the file, so a large clip is not held until the next collection.
 */
export function readVideoMetadata(
  file: Blob,
  signal?: AbortSignal,
): Promise<VideoMetadata> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(failure("AbortError", "The check was cancelled."));
      return;
    }
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    const finish = () => {
      clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
      video.onloadedmetadata = null;
      video.onerror = null;
      video.removeAttribute("src");
      video.load();
      URL.revokeObjectURL(url);
    };
    const onAbort = () => {
      finish();
      reject(failure("AbortError", "The check was cancelled."));
    };
    const timer = setTimeout(() => {
      finish();
      reject(failure("TimeoutError", "The browser took too long."));
    }, READ_TIMEOUT_MS);
    signal?.addEventListener("abort", onAbort);
    video.preload = "metadata";
    video.muted = true;
    video.onloadedmetadata = () => {
      const metadata = { duration: video.duration, width: video.videoWidth };
      finish();
      resolve(metadata);
    };
    video.onerror = () => {
      finish();
      reject(failure("MediaError", "The browser could not read the video."));
    };
    video.src = url;
  });
}
